import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { extendDefaultOptions, agDateColumnFilter } from "../utility/gridHelper";
import { PreprocessDateTime } from "../utility/view-field-mapping";
import { Demand } from "../generated-models/Demand";
import { ProjectListRecord } from "../npr-request.model";

@Component({
  standalone: false,
  templateUrl: "./demands.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintDemandsComponent extends GridViewBaseComponent implements OnInit {
  // Required by base class
  gridOptions!: GridOptions<Demand>;
  demandGridOptions!: GridOptions<Demand>;
  demands: Demand[] = [];
  rowData: Demand[] = [];
  initPageSize = 15;
  selectedProject!: ProjectListRecord;
  projects: ProjectListRecord[] = [];
  keyFilterStats: string = "filter:capacity-blueprint-demands";
  private isLoadingDemands = false;

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit() {
    this.initializeGridOptions();
    
    
    try {
      // Load projects
      const projectsResponse = await this.apiService.getProjects().toPromise();
      if (projectsResponse) {
        this.projects = projectsResponse.map((p) => ({
          ...p,
          Label: `${p.Title} (${p.ProjectOid}) - ${p.ProjectType}`,
        } as ProjectListRecord));
      }

      // Deep link: auto-select a project when a project id is provided in the URL.
      const projectIdParam = this.route.snapshot.queryParamMap.get("project");
      if (projectIdParam) {
        const projectId = Number(projectIdParam);
        const matchedProject = this.projects.find((p) => p.Id === projectId);
        if (matchedProject) {
          this.selectedProject = matchedProject;
          await this.onProjectChanged();
        } else {
          this.notificationService.warning(`Project with id ${projectIdParam} was not found.`);
        }
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.notificationService.error(`Failed to load data: ${message}`);
    } finally {
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  private initializeGridOptions(): void {
    const demandColumns: ColDef[] = [
      {
        headerName: "",
        field: "Id",
        filter: "agNumberColumnFilter",
        width: 100,
        cellRenderer: (params: ICellRendererParams<Demand>) => {
          const demand = params.data;
          if (!demand) {
            return "";
          }
          const link = document.createElement("a");
          const urlTree = this.router.createUrlTree([
            "quota/capacity/demand/serviceTreeId",
            demand.ServiceTreeId,
            "type",
            demand.Type,
            "name",
            demand.BlueprintName,
            "region",
            demand.RegionName,
          ], {
            queryParams:
            {
              ID: demand.Id
            }
          });
          link.href = this.router.serializeUrl(urlTree);
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.className = "grid-link";
          link.innerText = "View Details";
          return link;
        },
      },
      {
        headerName: "Service Tree",
        field: "ServiceTeam",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          const team = params.data?.ServiceTeam ?? "";
          const id = params.data?.ServiceTreeId ?? "";
          return team ? `${team}(${id})` : id;
        },
      },
      { headerName: "Name", field: "BlueprintName", filter: "agTextColumnFilter" },
      { headerName: "Commit Hash", field: "CommitHash", filter: "agTextColumnFilter" },
      { headerName: "Status", field: "Status", filter: "agTextColumnFilter" },
      { headerName: "Customer Contact", field: "CustomerContact", filter: "agTextColumnFilter" },
      { headerName: "Customer Type", field: "CustomerType", filter: "agTextColumnFilter" },
      { headerName: "Demand Growth Type", field: "DemandGrowthType", filter: "agTextColumnFilter" },
      { headerName: "Demand Origin", field: "DemandOrigin", filter: "agTextColumnFilter" },
      { headerName: "Demand Source", field: "DemandSource", filter: "agTextColumnFilter" },
      { headerName: "Source System", field: "SourceSystem", filter: "agTextColumnFilter" },
      { headerName: "Business Priority", field: "BusinessPriority", filter: "agTextColumnFilter" },
      { headerName: "Contract", field: "Contract", filter: "agTextColumnFilter" },
      { headerName: "Platform Scenario", field: "PlatformScenario", filter: "agTextColumnFilter" },
      {
        headerName: "Created Time(UTC)",
        field: "CreatedTime",
        cellRenderer: (params: any) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight: any, cellValue: any) => agDateColumnFilter(filterLocalDateAtMidnight, cellValue),
        },
      },
    ];

    this.demandGridOptions = extendDefaultOptions({
      columnDefs: demandColumns,
      paginationPageSize: this.initPageSize,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
    });

    this.gridOptions = this.demandGridOptions;
  }
  
  async onProjectChanged(): Promise<void> {
    if (!this.selectedProject) {
      this.demands = [];
      this.rowData = [];
      this.updateProjectQueryParam(null);
      this.cdr.detectChanges();
      return;
    }

    this.updateProjectQueryParam(this.selectedProject.Id);

    this.isLoadingDemands = true;
    this.loadingService.setLoading(true);
    try {
      const demandsResponse = await this.apiService.getDemandsByProjectId(this.selectedProject.Id).toPromise();
      this.demands = demandsResponse ?? [];
      this.rowData = this.demands;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.notificationService.error(`Failed to load demands: ${message}`);
      this.demands = [];
      this.rowData = [];
    } finally {
      this.isLoadingDemands = false;
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();

      setTimeout(() => {
        this.gridApi?.autoSizeAllColumns();
      }, 100);
    }
  }
  
  private async loadAllData(): Promise<void> {
    if (!this.selectedProject) {
      return;
    }

    this.loadingService.setLoading(true);
    try {
      const allDemands = await this.apiService.getDemandsByProjectId(this.selectedProject.Id).toPromise();
      this.demands = allDemands ?? [];
      this.rowData = this.demands;

      setTimeout(() => {
        this.gridApi?.autoSizeAllColumns();
      }, 100);
    } catch (error: any) {
      this.notificationService.error(`Failed to load data: ${error.message}`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi = params.api;
    // Hide loading overlay and show empty state since no project is selected yet
    this.gridApi.hideOverlay();
  }

  async refreshData(): Promise<void> {
    await this.loadAllData();
  }

  /**
   * Updates the `project` query parameter in the URL so the current selection is shareable.
   * @param projectId The selected project id, or `null` to clear the parameter.
   */
  private updateProjectQueryParam(projectId: number | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { project: projectId },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  @HostListener("window:resize")
  onResize(): void {
    setTimeout(() => {
      this.gridApi?.autoSizeAllColumns();
    }, 100);
  }
}
